import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { GRID_CONFIG } from '../gridConfig';
import { RelativesMapData } from '../components/Map3Relatives';
import { ColumnInfo } from './layoutMath';
import { getUniqueIntervals } from '../../utils/intervalUtils';
import { doIntervalsOverlap } from '../../utils/comparisonUtils';

/**
 * Returns the total width of the master ReactFlow grid in shared timeline mode.
 * This is the sum of:
 * 1. The objective column width (fixed)
 * 2. The total width of all columns in the shared timeline
 *
 * All statucolumns have the same width (GRID_CONFIG.cellWidth) for the current intervalType.
 */
export function getTotalGridWidthSharedTimeline<
  T extends { intervals: Array<{ start: Date; end: Date }> },
>(mapsIntervals: T[], intervalType: MilestoneMapInterval): number {
  // Get all intervals from all maps
  const allIntervals = mapsIntervals.flatMap((m) => m.intervals);

  // Get unique intervals adjusted to the requested type
  const uniqueAdjustedIntervals = getUniqueIntervals(allIntervals, intervalType);

  // Each interval is a column of fixed width
  const totalTimelineWidth = uniqueAdjustedIntervals.length * GRID_CONFIG.cellWidth;
  return GRID_CONFIG.objectiveWidth + totalTimelineWidth;
}

/**
 * Returns all unique intervals of a specific type across all maps.
 *
 * This function:
 * 1. Collects all intervals from all relative maps
 * 2. Delegates to getUniqueIntervals to generate a standardized set of intervals matching the requested type
 * 3. Returns a uniform array of intervals that spans from the earliest to latest date across all maps
 *
 * @param relativeMaps - The relative maps to get the unique intervals from
 * @param intervalType - The desired interval type for output (hours, days, weeks, months, quarters, years)
 * @returns A sorted array of uniform intervals of the requested type spanning all maps
 */
export function getUniqueIntervalsFromRelativesMaps(
  relativeMaps: RelativesMapData[],
  intervalType: MilestoneMapInterval,
): { start: Date; end: Date }[] {
  // Step 1: Collect all intervals from all maps
  const allIntervals = relativeMaps.flatMap((m) => m.adjustedIntervals);

  // Step 2: Use getUniqueIntervals to generate standardized intervals of the requested type
  // This will find the min/max dates and create uniform intervals spanning the entire range
  const uniqueIntervals = getUniqueIntervals(allIntervals, intervalType);

  return uniqueIntervals;
}

/**
 * Returns an array of column metadata for the shared timeline.
 * Each object contains width, start/end, master grid index, interval type, and X positions.
 *
 * In shared timeline mode, ALL maps must use the SAME set of intervals (columns),
 * regardless of their original interval granularity. This ensures that columns
 * (e.g., months) align perfectly across all maps, so that, for example, "June"
 * is the same for every map.
 *
 * For each map, we do NOT generate new intervals from the map's own data. Instead,
 * we use the global intervals as the columns for all maps. For each global interval,
 * we check if the map has any data in that interval. If so, we mark it as active for that map.
 *
 * This approach is necessary because maps with different default intervals (e.g., weeks)
 * may start on different dates, and generating intervals from their own data would cause
 * their columns to be out of sync with the shared timeline and with each other.
 *
 * @param milestoneMaps - The milestone maps included in the grid, containing milestoneMap uuid and adjustedIntervals
 * @param intervalType - The interval type to use for all columns (adjustedIntervals should all be adjusted to this interval type)
 * @param milestoneMapUuid - Optional UUID of a specific milestone map to focus on. If provided,
 *                          the columns info will include specific status column indices for this map.
 * @returns An array of column metadata for the shared timeline.
 */
export function getSharedTimelineColumnsInfo<
  T extends { milestoneMap: { uuid: string }; intervals: Array<{ start: Date; end: Date }> },
>(milestoneMaps: T[], intervalType: MilestoneMapInterval, milestoneMapUuid?: string): ColumnInfo[] {
  // Get all unique intervals across all maps, adjusted to the requested interval type
  // These are the global intervals that ALL maps will use as columns in shared timeline mode.
  const allIntervals = milestoneMaps.flatMap((m) => m.intervals);
  const uniqueAdjustedIntervals = getUniqueIntervals(allIntervals, intervalType);

  // If no milestoneMapUuid, return columns info for the whole grid (no status column info)
  if (!milestoneMapUuid) {
    return uniqueAdjustedIntervals.map((interval, idx) => ({
      width: GRID_CONFIG.cellWidth,
      startDate: interval.start,
      endDate: interval.end,
      // In shared timeline master column index is relative to the master grid.
      // Status column index is relative to the current map.
      masterColumnIndex: idx,
      // statusColumnIndex is null if no milestoneMapUuid is provided.
      statusColumnIndex: null,
      columnIntervalUnit: intervalType,
      globalX: GRID_CONFIG.objectiveWidth + idx * GRID_CONFIG.cellWidth,
      // localX is null if no milestoneMapUuid is provided.
      localX: null,
    }));
  }

  // Find the map requested
  const milestoneMapData = milestoneMaps.find((m) => m.milestoneMap.uuid === milestoneMapUuid);
  if (!milestoneMapData) {
    throw new Error(`Milestone map not found: ${milestoneMapUuid}`);
  }

  // For each global interval, check if the map has any data in that interval.
  let nextStatusIndex = 0; // Track the next available status index
  return uniqueAdjustedIntervals.map((interval, masterColumnIndex) => {
    // Does this map have any data in this interval?
    const hasOverlap = milestoneMapData.intervals.some((mi) => {
      return doIntervalsOverlap(interval, mi);
    });

    const statusColumnIndex = hasOverlap ? nextStatusIndex++ : null;

    return {
      width: GRID_CONFIG.cellWidth,
      startDate: interval.start,
      endDate: interval.end,
      masterColumnIndex,
      statusColumnIndex,
      columnIntervalUnit: intervalType,
      globalX: GRID_CONFIG.objectiveWidth + masterColumnIndex * GRID_CONFIG.cellWidth,
      localX:
        statusColumnIndex !== null
          ? GRID_CONFIG.objectiveWidth + statusColumnIndex * GRID_CONFIG.cellWidth
          : null,
    };
  });
}

/**
 * This function takes column info for a map and returns the x offset for the map's left edge.
 *
 * @param columnsInfo - The column info for the map.
 * @returns The x offset for the map's left edge.
 */
export function getMapSharedTimelineXOffsetForMap(columnsInfo: ColumnInfo[]): number {
  // Make sure columnInfo actually has a map's column info in it. (If it's just column info for
  // the master grid, return 0.)
  const mapColumnsInfo = columnsInfo.filter((col) => col.statusColumnIndex !== null);
  if (mapColumnsInfo.length === 0) return 0;
  const firstStatusColumn = mapColumnsInfo.find((col) => col.statusColumnIndex === 0);
  if (!firstStatusColumn) return 0;
  // The map's left edge is the x offset of the first status column minus the objective column width.
  return firstStatusColumn.globalX - GRID_CONFIG.objectiveWidth;
}
